import React, { useEffect, useState } from "react";

const STORAGE_KEY = "acos.landing_splash_seen";
const SPLASH_MS = 2200;

/**
 * Landing-only splash shown once per browser (localStorage flag).
 * Pure CSS, no library dependency. Renders a centred logo with a
 * subtle pulse + accent halo, then fades out. The short tagline
 * is centred under the mark to make the moment feel intentional
 * rather than a loading delay.
 *
 * Skip rules:
 *   • localStorage flag already set → never show again
 *   • `prefers-reduced-motion` → user opt-out, skip immediately
 */
export default function LandingSplash() {
  const initiallyEnabled = (() => {
    if (typeof window === "undefined") return false;
    try {
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
      return !window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return false;
    }
  })();
  const [visible, setVisible] = useState(initiallyEnabled);

  useEffect(() => {
    if (!visible) return;
    const out = setTimeout(() => {
      setVisible(false);
      try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* private mode */ }
    }, SPLASH_MS);
    return () => clearTimeout(out);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      aria-hidden
      data-testid="landing-splash"
      className="landing-splash-overlay fixed inset-0 z-[80] flex items-center justify-center bg-background"
    >
      <div className="flex flex-col items-center gap-5 px-6 text-center">
        <img
          src="/logo.png"
          alt=""
          className="landing-splash-logo h-28 w-28 object-contain"
          draggable={false}
        />
        <div className="landing-splash-text flex flex-col items-center gap-2">
          <div className="font-display text-3xl font-bold tracking-tight">
            Appel<span className="text-[hsl(var(--accent))]">24/7</span>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
            Votre établissement, disponible 24/7
          </div>
        </div>
      </div>
    </div>
  );
}
